import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tab } from 'semantic-ui-react';
import { useLocalStorage } from 'usehooks-ts';
import { countryToFlag, getLocationInfo } from '../../../../utils/location';
import { usePromise } from '../../../_shared/react-hooks';
import SizeTable from './SizeTable';
import { generateTableHeaders, generateTableRows } from './SizeChartDataProcessing';
import HeightSelection from './HeightSelection';
import { notNil } from '../../../../utils/typescriptUtils';
import AloDropdown, { DropdownItem } from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloDropdown';
import QuickFit from '../QuickFit';
import { isSiteInEnglish } from 'src/scripts/utils/helpers';
import { SizeChartData, SizeGuideTable, SystemOfMeasurements } from './sizeChartTypes';
import TabBottomContent from './TabBottomContent';
import { fetchDataFromBuilder, getBuilderCacheBusterKey } from 'src/scripts/utils/builder';
import './style.scss';

enum SizeGuideBuilderKey {
  Women = 'size-guide-women',
  Men = 'size-guide-men',
}

interface UserPreferredSizeSettings {
  currentCountry: string;
  systemOfMeasurements: SystemOfMeasurements;
  height?: string;
}

interface SizeGuideContentProps {
  gender: 'men' | 'women';
  category?: string;
  subcategory?: string;
  quickFitText?: string | null;
}

const SizeGuideContent: React.FC<SizeGuideContentProps> = ({ gender, category, subcategory, quickFitText }) => {
  const [sizeChartData, setSizeChartData] = useState<SizeChartData | null>(null);
  const [isLoadingSizeChart, setIsLoadingSizeChart] = useState(true);

  useEffect(() => {
    const key = gender === 'men' ? SizeGuideBuilderKey.Men : SizeGuideBuilderKey.Women;
    setIsLoadingSizeChart(true);
    getBuilderData(key).then((data) => {
      setSizeChartData(data);
      setIsLoadingSizeChart(false);
    });
  }, [gender]);

  const sizeGuideTabActiveTabs = useMemo(() => sizeChartData?.tabs?.filter((tab) => !tab?.disable), [
    sizeChartData?.tabs,
  ]);

  const supportedCountries = useMemo(() => sizeChartData?.supportedCountries || [], [
    sizeChartData?.supportedCountries,
  ]);

  const [userPreferredSelections, setUserPreferredSelection] = useLocalStorage<UserPreferredSizeSettings | null>(
    `alo-users-preffered-size-settings`,
    null
  );

  const { data: locationInfo, isLoading: isLoadingLocationInfo, error: getLocationError } = usePromise(getLocationInfo);

  useEffect(() => {
    if (!userPreferredSelections && !isLoadingLocationInfo && locationInfo) {
      const countryCode = locationInfo.country_code.toLowerCase();
      const isSupported = supportedCountries.some((option) => option.key.toLowerCase() === countryCode);

      if (isSupported) {
        setUserPreferredSelection({
          currentCountry: countryCode,
          systemOfMeasurements: countryCode === 'us' ? 'imperial' : 'metric',
        });
      } else {
        // Outside of US but not supported country
        setUserPreferredSelection({
          currentCountry: 'us',
          systemOfMeasurements: 'metric',
        });
      }
    } else if (getLocationError && !userPreferredSelections) {
      // Default to US and Imperial
      setUserPreferredSelection({
        currentCountry: 'us',
        systemOfMeasurements: 'imperial',
      });
    }
  }, [
    userPreferredSelections,
    isLoadingLocationInfo,
    locationInfo,
    supportedCountries,
    getLocationError,
    setUserPreferredSelection,
  ]);

  const toggleMeasurementSystem = useCallback(
    (position: string) => {
      if (!userPreferredSelections) return;
      setUserPreferredSelection({
        ...userPreferredSelections,
        systemOfMeasurements: position === 'right' ? 'imperial' : 'metric',
      });
    },
    [userPreferredSelections, setUserPreferredSelection]
  );

  const setUsersHeight = useCallback(
    (height: string) => {
      if (!userPreferredSelections) return;
      setUserPreferredSelection({
        ...userPreferredSelections,
        height,
      });
    },
    [userPreferredSelections, setUserPreferredSelection]
  );

  const getActiveTabIndex = useCallback(
    (category: string, subcategory: string): number => {
      const index = sizeGuideTabActiveTabs?.findIndex(
        (tab) => tab.productTypes?.includes(category) || tab.productTypes?.includes(subcategory)
      );
      return index !== undefined && index > -1 ? index : 0;
    },
    [sizeGuideTabActiveTabs]
  );

  const activeTabIndex = useMemo(
    () => (category || subcategory ? getActiveTabIndex(category || '', subcategory || '') : 0),
    [category, subcategory, getActiveTabIndex]
  );

  const dropdownItems = useMemo(
    () =>
      supportedCountries.map(({ key, text, flag }) => ({
        key,
        text,
        flag,
      })),
    [supportedCountries]
  );

  const dropdownDefaultValue = useMemo(
    () => supportedCountries.find((country) => country.key === userPreferredSelections?.currentCountry),
    [supportedCountries, userPreferredSelections?.currentCountry]
  );

  const dropdownValue = useMemo(
    () => ({
      key: dropdownDefaultValue?.key || '',
      text: dropdownDefaultValue?.text || '',
      flag: dropdownDefaultValue?.flag || '',
    }),
    [dropdownDefaultValue]
  );

  const onDropdownChange = useCallback(
    (selectedCountry: DropdownItem) => {
      if (!userPreferredSelections) return;
      setUserPreferredSelection({
        ...userPreferredSelections,
        systemOfMeasurements: selectedCountry?.key === 'us' ? 'imperial' : 'metric',
        currentCountry: selectedCountry?.key as string,
      });
    },
    [userPreferredSelections, setUserPreferredSelection]
  );

  const renderOption = useCallback(
    (option: DropdownItem) => (
      <>
        {option?.flag ? <span>{countryToFlag(option.flag)}</span> : null}
        <span style={{ marginLeft: '1rem' }}>{option.text}</span>
      </>
    ),
    []
  );

  const selectedCountry =
    userPreferredSelections?.currentCountry === 'uk' ? 'gb' : userPreferredSelections?.currentCountry;
  const flagElement = userPreferredSelections?.currentCountry ? (
    <span>{countryToFlag(selectedCountry || '')}</span>
  ) : null;

  const panes = useMemo(() => {
    if (!sizeGuideTabActiveTabs) return [];
    return sizeGuideTabActiveTabs
      .map((tab, index) => {
        if (tab?.disable) return null;

        const { showBraGuide, showLengthGuide } = tab;

        return {
          menuItem: {
            key: tab.id,
            content: tab.title,
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (['Enter', ' '].includes(e.key)) {
                const focusedTab = e.currentTarget;
                (focusedTab as HTMLElement).click();
              }
            },
          },
          render: () => (
            <Tab.Pane key={tab.id} attached={false} className="size-guide-tab">
              {quickFitText && activeTabIndex === index ? <QuickFit quickFitText={quickFitText} tabIndex={0} /> : null}
              {userPreferredSelections && (
                <>
                  {tab.tables?.map((table) => (
                    <SizeGuideTableBlock
                      key={table.title}
                      table={table}
                      userPreferredSelections={userPreferredSelections}
                      toggleMeasurementSystem={toggleMeasurementSystem}
                    />
                  ))}
                  {showBraGuide && sizeChartData?.braGuide?.table && (
                    <SizeGuideTableBlock
                      table={sizeChartData.braGuide.table}
                      userPreferredSelections={userPreferredSelections}
                      toggleMeasurementSystem={toggleMeasurementSystem}
                    />
                  )}
                  {showLengthGuide && sizeChartData?.lengthGuide && (
                    <>
                      <SizeGuideTableBlock
                        table={sizeChartData.lengthGuide.lengthGuideTable}
                        userPreferredSelections={userPreferredSelections}
                        toggleMeasurementSystem={toggleMeasurementSystem}
                      />
                      <HeightSelection
                        data={sizeChartData.lengthGuide.heightSelectionTab}
                        systemOfMeasurements={userPreferredSelections.systemOfMeasurements}
                        usersHeight={userPreferredSelections.height}
                        setHeight={setUsersHeight}
                      />
                    </>
                  )}
                  <TabBottomContent sizeGuideTab={[tab.id]} />
                </>
              )}
            </Tab.Pane>
          ),
        };
      })
      .filter(notNil);
  }, [
    sizeChartData,
    sizeGuideTabActiveTabs,
    activeTabIndex,
    quickFitText,
    userPreferredSelections,
    toggleMeasurementSystem,
    setUsersHeight,
  ]);

  if (!userPreferredSelections || isLoadingSizeChart) {
    return (
      <div className="pdp-size-guide-loader">
        <span className="body">Loading...</span>
      </div>
    );
  }

  if (!sizeChartData) return <FallbackView />;

  return (
    <div className="size-guide-content">
      <div className="size-guide-dropdown-wrapper">
        <AloDropdown
          searchable
          inputReadOnly={!isSiteInEnglish()}
          searchableDropdownPrependIcon={flagElement}
          dropdownItems={dropdownItems}
          onChange={onDropdownChange}
          placeholder="Select Country"
          value={dropdownValue}
          wrapperClass="size-guide-dropdown"
          MUIAutocompleteProps={{
            disableClearable: true,
            renderOption,
          }}
        />
      </div>
      <Tab
        className="size-guide-tabs"
        defaultActiveIndex={activeTabIndex || 0}
        menu={{ secondary: true, pointing: true }}
        panes={panes}
      />
    </div>
  );
};

export default SizeGuideContent;

interface SizeGuideTableBlockProps {
  table: SizeGuideTable;
  userPreferredSelections: UserPreferredSizeSettings;
  toggleMeasurementSystem: (position: string) => void;
}

const SizeGuideTableBlock: React.FC<SizeGuideTableBlockProps> = ({
  table,
  userPreferredSelections,
  toggleMeasurementSystem,
}) => {
  const { title: tableTitle, columnsSize = 0 } = table;

  if (columnsSize === 0) return null;

  const rows = generateTableRows({
    table,
    currentCountry: userPreferredSelections.currentCountry,
    systemOfMeasurements: userPreferredSelections.systemOfMeasurements,
  });

  const headers = generateTableHeaders({
    table,
    systemOfMeasurements: userPreferredSelections.systemOfMeasurements,
    toggleCallback: toggleMeasurementSystem,
  });

  return (
    <React.Fragment>
      {tableTitle && (
        <h6 className="h6 uppercase size-guide-section-title" tabIndex={0}>
          {tableTitle}
        </h6>
      )}
      <SizeTable headerRows={headers} tableData={rows} />
    </React.Fragment>
  );
};

const FallbackView = () => (
  <div className="pdp-size-guide-fallback">
    <p className="body mb-sm">Unable to load Size Guide</p>
    <p className="body-small grey">Please refresh the page or try again later</p>
  </div>
);

const getBuilderData = (key: SizeGuideBuilderKey) =>
  fetchDataFromBuilder<SizeChartData>(key, {
    options: { cb: getBuilderCacheBusterKey() },
  })
    .then((resp) => {
      return resp?.data;
    })
    .catch(() => null);
